import { useState } from 'react';

interface Template {
  id: string;
  name: string;
  sector: string;
  description: string;
  icon: string;
  preview: {
    impactGoals: string[];
    outcomes: string[];
    outputs: string[];
    activities: string[];
    inputs: string[];
  };
}

const TEMPLATES: Template[] = [
  {
    id: 'workforce-development',
    name: 'Workforce Development',
    sector: 'Economic Empowerment',
    description: 'Job training, career readiness, and employment placement programs',
    icon: '💼',
    preview: {
      impactGoals: ['Economic mobility in underserved communities'],
      outcomes: [
        'Participants gain job-ready skills',
        'Participants secure stable employment',
        'Participants achieve wage growth',
      ],
      outputs: [
        'Training sessions conducted',
        'Participants completing program',
        'Job placements achieved',
      ],
      activities: [
        'Job readiness workshops',
        'Technical skills training',
        'Career counseling',
        'Employer networking events',
      ],
      inputs: [
        'Training curriculum',
        'Certified instructors',
        'Training facility',
        'Employer partnerships',
      ],
    },
  },
  {
    id: 'youth-education',
    name: 'Youth Education',
    sector: 'Education',
    description: 'After-school programs, tutoring, and academic support for students',
    icon: '📚',
    preview: {
      impactGoals: ['Improved educational outcomes for underserved youth'],
      outcomes: [
        'Students improve academic performance',
        'Students develop positive attitudes toward learning',
        'Students graduate high school on time',
      ],
      outputs: [
        'Tutoring hours provided',
        'Students served',
        'Workshops conducted',
      ],
      activities: [
        'One-on-one tutoring',
        'Homework help sessions',
        'STEM enrichment activities',
        'College prep workshops',
      ],
      inputs: [
        'Volunteer tutors',
        'Learning materials',
        'Safe learning space',
        'Staff coordinator',
      ],
    },
  },
  {
    id: 'food-security',
    name: 'Food Security',
    sector: 'Health & Nutrition',
    description: 'Food pantries, meal programs, and nutrition education',
    icon: '🍎',
    preview: {
      impactGoals: ['Reduced food insecurity in target community'],
      outcomes: [
        'Families have consistent food access',
        'Improved nutrition knowledge',
        'Reduced household food stress',
      ],
      outputs: [
        'Meals distributed',
        'Families served',
        'Nutrition classes held',
      ],
      activities: [
        'Weekly food distribution',
        'Home delivery for seniors',
        'Nutrition education workshops',
        'SNAP enrollment assistance',
      ],
      inputs: [
        'Food donations',
        'Volunteers',
        'Distribution facility',
        'Refrigerated storage',
      ],
    },
  },
  {
    id: 'health-services',
    name: 'Community Health',
    sector: 'Health & Wellness',
    description: 'Health screenings, wellness programs, and access to care',
    icon: '🏥',
    preview: {
      impactGoals: ['Improved health outcomes in underserved populations'],
      outcomes: [
        'Increased health screenings',
        'Better chronic disease management',
        'Improved health literacy',
      ],
      outputs: [
        'Screenings conducted',
        'Patients served',
        'Health education sessions',
      ],
      activities: [
        'Mobile health clinics',
        'Chronic disease management classes',
        'Health navigation services',
        'Preventive care outreach',
      ],
      inputs: [
        'Medical professionals',
        'Mobile clinic unit',
        'Health education materials',
        'Community health workers',
      ],
    },
  },
];

interface TemplateLibraryProps {
  onSelect: (template: Template) => void;
  onClose: () => void;
}

export function TemplateLibrary({ onSelect, onClose }: TemplateLibraryProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTemplates = TEMPLATES.filter(
    t =>
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.sector.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">📋 Template Library</h2>
                <p className="text-sm text-gray-500 mt-1">Start with a proven framework for your sector</p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Search */}
            <div className="mt-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search templates..."
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {selectedTemplate ? (
              /* Template Preview */
              <div>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-800 mb-4"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to templates
                </button>

                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">{selectedTemplate.icon}</span>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{selectedTemplate.name}</h3>
                    <p className="text-sm text-gray-500">{selectedTemplate.sector}</p>
                  </div>
                </div>

                <p className="text-gray-600 mb-6">{selectedTemplate.description}</p>

                {/* Preview Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <PreviewSection title="🎯 Impact Goals" items={selectedTemplate.preview.impactGoals} color="blue" />
                  <PreviewSection title="📈 Outcomes" items={selectedTemplate.preview.outcomes} color="amber" />
                  <PreviewSection title="📊 Outputs" items={selectedTemplate.preview.outputs} color="purple" />
                  <PreviewSection title="⚡ Activities" items={selectedTemplate.preview.activities} color="green" />
                  <PreviewSection title="📦 Inputs" items={selectedTemplate.preview.inputs} color="orange" className="md:col-span-2" />
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => onSelect(selectedTemplate)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                  >
                    Use This Template
                  </button>
                </div>
              </div>
            ) : (
              /* Template Grid */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md cursor-pointer transition-all"
                    onClick={() => setSelectedTemplate(template)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-3xl">{template.icon}</span>
                      <div>
                        <h3 className="font-medium text-gray-900">{template.name}</h3>
                        <p className="text-xs text-gray-500">{template.sector}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">{template.description}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                        {template.preview.activities.length} activities
                      </span>
                      <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                        {template.preview.outcomes.length} outcomes
                      </span>
                    </div>
                  </div>
                ))}

                {filteredTemplates.length === 0 && (
                  <div className="col-span-2 text-center py-8 text-gray-500">
                    No templates match your search
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Templates provide a starting point — customize to fit your program
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Start from Scratch
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewSection({ 
  title, 
  items, 
  color, 
  className = '' 
}: { 
  title: string; 
  items: string[]; 
  color: string;
  className?: string;
}) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 border-blue-200',
    amber: 'bg-amber-50 border-amber-200',
    purple: 'bg-purple-50 border-purple-200',
    green: 'bg-green-50 border-green-200',
    orange: 'bg-orange-50 border-orange-200',
  };

  return (
    <div className={`rounded-lg border p-3 ${colorClasses[color]} ${className}`}>
      <h4 className="font-medium text-sm text-gray-900 mb-2">{title}</h4>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="text-sm text-gray-600 flex items-start">
            <span className="mr-2">•</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Export template type for use elsewhere
export type { Template };
